import {
  faInfoCircle,
  faPen,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmptyList from 'components/common/EmptyList';
import CustomPropertyRegistrationModal from 'components/modals/CustomPropertyRegistrationModal';
import DialogModal from 'components/modals/DialogModal';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import useApi from 'helpers/api';
import { dattoDeviceProperties } from 'helpers/utils';
import { useEffect, useState } from 'react';
import { Button, Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';

const CustomFieldsList = ({ entityType, assetTypeId, hasIntegration }) => {
  const api = useApi();
  const [customFields, setCustomFields] = useState([]);
  const [mappingCache, setMappingCache] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  const [error, setError] = useState({});
  const [success, setSuccess] = useState('');
  const [selectedCustomField, setSelectedCustomField] = useState({});

  useEffect(() => {
    getCustomFields();
  }, [entityType, assetTypeId, success]);

  const getCustomFields = async () => {
    console.log('Carregando custom fields:', { entityType, assetTypeId });
    const response = await api.customFields.list(entityType, assetTypeId);
    console.log('Resposta da API:', response);
    if (Array.isArray(response)) {
      setCustomFields(response);
    } else {
      console.error('Resposta não é array:', response);
      setCustomFields([]);
    }
  };

  const deleteCustomField = async response => {
    if (response) {
      const request = await api.customFields.delete(selectedCustomField.id);

      if (request.ok) {
        setSuccess('Campo personalizado excluído com sucesso');
        setShowSuccess(true);
        setSelectedCustomField(null);
      } else {
        setError(request);
        setShowError(true);
      }
      setShowDialog(false);
    }
  };

  const checkIfThereIsMapping = async customFieldId => {
    const response = await api.assetTypes.customFields.dattoFieldMapping.find(
      assetTypeId,
      customFieldId
    );

    setMappingCache(prevCache => ({
      ...prevCache,
      [customFieldId]: response
    }));

    return response;
  };

  const mapFieldToDatto = async (customFieldId, targetField) => {
    const body = { targetField };
    const response = await api.assetTypes.customFields.dattoFieldMapping.map(
      assetTypeId,
      customFieldId,
      body
    );

    if (response.ok) {
      setSuccess('Campo mapeado com sucesso');
      setShowSuccess(true);
    } else {
      setError(response);
      setShowError(true);
    }
  };

  const unmapFieldFromDatto = async customFieldId => {
    const response = await api.assetTypes.customFields.dattoFieldMapping.unmap(
      assetTypeId,
      customFieldId
    );

    if (response.ok) {
      setSuccess('Campo desmapeado com sucesso');
      setShowSuccess(true);
    } else {
      setError(response);
      setShowError(true);
    }
  };

  return (
    <>
      <DialogModal
        message="Tem certeza que deseja excluir o campo personalizado?"
        showModal={showDialog}
        handleClose={() => setShowDialog(false)}
        response={deleteCustomField}
      />
      <SuccessRequest
        showAlert={showSuccess}
        setShowAlert={() => setShowSuccess(false)}
        message={success}
      />
      <FailedRequest
        showAlert={showError}
        setShowAlert={() => setShowError(false)}
        message={error.userMessage}
      />
      <CustomPropertyRegistrationModal
        show={showModal}
        close={() => setShowModal(false)}
        refresh={() => getCustomFields()}
        entityType={entityType}
        assetTypeId={assetTypeId}
        data={selectedCustomField || null}
        setData={setSelectedCustomField}
        isCreate={isCreate}
      />
      {customFields.length > 0 ? (
        <Table striped hover responsive className="fs-10 mb-0 overflow-hidden">
          <thead className="text-nowrap align-middle">
            <tr>
              <th className={`text-900 ${!hasIntegration && 'w-75'}`}>Nome</th>
              {hasIntegration && (
                <th className="text-900">
                  <span className="me-1">Prop. Disp. Datto RMM</span>
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        Mapeie sua propriedade com uma propriedade de
                        dispositivo do Datto RMM para que os valores sejam
                        preenchidos automaticamente na criação de um novo ativo
                        (opcional).
                      </Tooltip>
                    }
                  >
                    <span>
                      <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                    </span>
                  </OverlayTrigger>
                </th>
              )}
              <th className="text-900">Tipo</th>
              <th className="text-900">Obrigatório</th>
              <th className="text-900"></th>
            </tr>
          </thead>
          <tbody className="align-middle white-space-nowrap">
            {customFields.map(field => (
              <tr key={field.id}>
                <td>{field.name}</td>
                {hasIntegration && (
                  <td>
                    <IntegrationField
                      id={field.id}
                      dataType={field.dataType}
                      checkIfThereIsMapping={checkIfThereIsMapping}
                      mapFieldToDatto={mapFieldToDatto}
                      unmapFieldFromDatto={unmapFieldFromDatto}
                    />
                  </td>
                )}
                <td>{field.dataType}</td>
                <td>
                  {field.required ? (
                    <span className="text-success">Sim</span>
                  ) : (
                    <span className="text-danger">Não</span>
                  )}
                </td>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setSelectedCustomField(field);
                      setShowModal(true);
                      setIsCreate(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} size="sm" />
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setShowDialog(true);
                      setSelectedCustomField(field);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="sm" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyList imgSize={300} vhSize={40} />
      )}
    </>
  );
};

const IntegrationField = ({
  id,
  dataType,
  checkIfThereIsMapping,
  mapFieldToDatto,
  unmapFieldFromDatto
}) => {
  const [body, setBody] = useState({
    customFieldId: id,
    targetField: '',
    mapId: ''
  });

  const [isFetched, setIsFetched] = useState(false);

  useEffect(() => {
    const fetchMapping = async () => {
      if (!isFetched) {
        const response = await checkIfThereIsMapping(id);

        if (response && response.id) {
          setBody(prevBody => ({
            ...prevBody,
            targetField: response.targetField,
            mapId: response.id
          }));
        }

        setIsFetched(true);
      }
    };

    fetchMapping();
  }, [id, isFetched]);

  const handleChange = async e => {
    const newValue = e.target.value;
    setBody(prevBody => ({
      ...prevBody,
      targetField: newValue
    }));

    if (newValue) {
      await mapFieldToDatto(id, newValue);
    } else {
      await unmapFieldFromDatto(id);
    }
  };

  return (
    <Form.Select value={body.targetField} onChange={handleChange}>
      <option value="" key="" className="text-danger">
        Não mapeado
      </option>
      {dattoDeviceProperties
        .filter(property => property.dataType === dataType)
        .map(property => (
          <option value={property.value} key={property.value}>
            {property.name}
          </option>
        ))}
    </Form.Select>
  );
};

export default CustomFieldsList;
